import { type JSX } from 'react';
import { useIntl } from 'react-intl';

import {
  ArrowSmallDownIcon,
  ArrowSmallUpIcon,
  SorterSmallDoubleIcon,
} from '@dynatrace/strato-icons';

import { Menu } from '../../../../navigation/menu/Menu.js';
import {
  useDataTable,
  useDataTableColumn,
} from '../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../messages.js';

/**
 * Built-in column action items that expose sorting controls in the column header actions menu.
 *
 * When sorting is the only available column action, the sorting items are rendered as direct entries.
 * Otherwise, they are placed in a "Sort" sub-menu to avoid cluttering the top-level menu.
 * The available sorting items are determined by the current sorting state.
 *
 * @param hasOtherColumnActions - Whether there are any user-defined column actions besides sorting.
 * @internal
 */
export function SortingColumnActions({
  hasOtherColumnActions,
}: Readonly<{
  hasOtherColumnActions: boolean;
}>): JSX.Element | null {
  const intl = useIntl();
  const table = useDataTable();
  const column = useDataTableColumn();

  if (!column.getCanSort()) {
    return null;
  }

  const columnIsSorted = column.getIsSorted();
  const columnCanMultiSort = column.getCanMultiSort();
  const sortingState = table.getState().sorting ?? [];

  const columnSortIndex = column.getSortIndex();
  const tableIsSorted = sortingState.length > 0;
  const tableIsSingleSorted = sortingState.length === 1;
  const tableIsMultiSorted = sortingState.length > 1;
  const columnIsSingleSorted = tableIsSingleSorted && columnSortIndex === 0;

  // Single-sort actions
  // Only show the sort action when the sort direction is not already applied, unless multi-sorting is applied.
  // With multi-sorting enabled the single-sort actions allow switching from multi-sort to single sort.
  const showSortAscending = columnIsSorted !== 'asc' || tableIsMultiSorted;
  const showSortDescending = columnIsSorted !== 'desc' || tableIsMultiSorted;
  const showClearSingleSort = !tableIsMultiSorted && !!columnIsSorted;

  const singleSortGroup = (
    <Menu.Group>
      {showSortAscending && (
        <Menu.Item
          onSelect={() => column.toggleSorting(false, false)}
          data-dt-name="ColumnActionSortAscending"
        >
          <Menu.Prefix>
            <ArrowSmallUpIcon />
          </Menu.Prefix>
          {!tableIsMultiSorted && columnIsSorted === 'desc'
            ? intl.formatMessage(dataTableMessages.changeToAscending)
            : intl.formatMessage(dataTableMessages.sortAscending)}
        </Menu.Item>
      )}
      {showSortDescending && (
        <Menu.Item
          onSelect={() => column.toggleSorting(true, false)}
          data-dt-name="ColumnActionSortDescending"
        >
          <Menu.Prefix>
            <ArrowSmallDownIcon />
          </Menu.Prefix>
          {!tableIsMultiSorted && columnIsSorted === 'asc'
            ? intl.formatMessage(dataTableMessages.changeToDescending)
            : intl.formatMessage(dataTableMessages.sortDescending)}
        </Menu.Item>
      )}
      {showClearSingleSort && (
        <Menu.Item
          onSelect={() => column.clearSorting()}
          data-dt-name="ColumnActionClearSort"
        >
          <Menu.Prefix>
            <SorterSmallDoubleIcon />
          </Menu.Prefix>
          {intl.formatMessage(dataTableMessages.clearSorting)}
        </Menu.Item>
      )}
    </Menu.Group>
  );

  // Multi-sort actions
  // Only show multi-sort actions when any other column than the current one is sorted.
  let multiSortGroup: JSX.Element | null = null;
  const showMultiSortGroup =
    columnCanMultiSort && tableIsSorted && !columnIsSingleSorted;

  if (showMultiSortGroup) {
    const showAddAscending = columnIsSorted !== 'asc';
    const showAddDescending = columnIsSorted !== 'desc';
    const showClearMultiSort = !!columnIsSorted;
    multiSortGroup = (
      <Menu.Group>
        <Menu.Label
          aria-label={intl.formatMessage(
            dataTableMessages.multiColumnSortingLabel,
          )}
        >
          {intl.formatMessage(dataTableMessages.multiColumnSortingLabel)}
        </Menu.Label>
        {showAddAscending && (
          <Menu.Item
            onSelect={() => column.toggleSorting(false, true)}
            data-dt-name="ColumnActionMultiSortAscending"
          >
            <Menu.Prefix>
              <ArrowSmallUpIcon />
            </Menu.Prefix>
            {columnIsSorted === 'desc'
              ? intl.formatMessage(dataTableMessages.changeToAscending)
              : intl.formatMessage(dataTableMessages.addAsAscending)}
          </Menu.Item>
        )}
        {showAddDescending && (
          <Menu.Item
            onSelect={() => column.toggleSorting(true, true)}
            data-dt-name="ColumnActionMultiSortDescending"
          >
            <Menu.Prefix>
              <ArrowSmallDownIcon />
            </Menu.Prefix>
            {columnIsSorted === 'asc'
              ? intl.formatMessage(dataTableMessages.changeToDescending)
              : intl.formatMessage(dataTableMessages.addAsDescending)}
          </Menu.Item>
        )}
        {showClearMultiSort && (
          <Menu.Item
            onSelect={() => column.clearSorting()}
            data-dt-name="ColumnActionClearSort"
          >
            <Menu.Prefix>
              <SorterSmallDoubleIcon />
            </Menu.Prefix>
            {intl.formatMessage(dataTableMessages.clearSorting)}
          </Menu.Item>
        )}
      </Menu.Group>
    );
  }

  if (hasOtherColumnActions) {
    return (
      <Menu.Sub>
        <Menu.SubTrigger>
          {intl.formatMessage(dataTableMessages.sortSubmenuTrigger)}
        </Menu.SubTrigger>
        <Menu.SubContent>
          {singleSortGroup}
          {multiSortGroup}
        </Menu.SubContent>
      </Menu.Sub>
    );
  }

  return (
    <>
      {singleSortGroup}
      {multiSortGroup}
    </>
  );
}
